import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useQuickLog } from '../hooks/useQuickLog'
import { NavLink, useNavigate } from 'react-router-dom'
import {
  LayoutGrid, LayoutDashboard, Users, Target, TrendingUp, BookUser,
  MessageSquare, Calendar, CheckSquare, ShieldCheck, FileText,
  CircleDollarSign, DollarSign, BarChart2, Settings, LogOut, Settings2,
  Users2, HelpCircle, Inbox, ClipboardList, Store, Banknote, Building2,
  Share2, Activity, Bell, Files, Scale, ArrowLeftRight, Award, Receipt,
  Zap, Flag, PieChart, Tag, Stethoscope, Mail, Search, Plus, ListChecks, Send, KeyRound,
} from 'lucide-react'
import { useSession } from '../hooks/useSession'
import { signOut } from '../utils/signOut'
import { getAgency } from '../api/agencies'
import { getUnreadCount } from '../api/notifications'
import { SmartViewLibrary } from './SmartViewLibrary'
import styles from './Sidebar.module.css'

const S = 20
const SW = 1.5
/** Whether "Everything else" is open in this browser. */
const MORE_KEY = 'reppr_nav_more_open'

type NavItem = {
  label: string
  to: string
  icon: () => JSX.Element
  badge?: number
  end?: boolean
  adminOnly?: boolean
}

type NavSection = {
  /** Section heading. Omitted for the first (unlabelled) group. */
  label?: string
  /** The heading is a button that folds the section; the state is remembered per browser. */
  collapsible?: boolean
  items: NavItem[]
}

/**
 * Information architecture.
 *
 * Oct 2026: five things in the rail, and everything else under one heading.
 * An agent opens Reppr to settle what is waiting (Needs you — the Desk), to
 * look at an athlete (The Book), to move a deal (Pipeline), to see money
 * (Money) and to see dates (Calendar). The other twelve screens still exist
 * at their routes and in the command palette; they stop competing for
 * attention. "Everything else" opens by default — nothing was taken away,
 * it was moved down — and remembers being closed. Partners takes a slot of
 * its own when the agency-wide partner screen exists.
 *
 * Aug 2026: collapsed from 35 items to 15. The audit found four jobs each smeared across several screens (money in
 * five places, messaging in five, reporting in seven, counterparties in
 * three), which was most of why the product felt large. Those screens still
 * exist at their original routes; they are now reached from a `SectionTabs`
 * strip on their group's main screen rather than from a nav slot each.
 *
 * Where each demoted item went:
 *   Invoices / Payments / Ledger / Transactions -> Money tabs
 *   Organizations / Network                     -> Contacts tabs
 *   Team chat / Email                           -> Inbox tabs
 *   Analytics / Builder / Portfolio / Player
 *     package / Win-loss                        -> Reports tabs
 *   Incentives                                  -> Contracts tabs
 *   Deal templates / Player moves               -> Pipeline tabs
 *   Activity / Notifications                    -> the Desk and the header bell
 *   Team / Tags / Vendors /
 *     Goals / Data health                       -> Settings
 */
const NAV_SECTIONS: NavSection[] = [
  {
    items: [
      { label: 'Needs you', to: '/dashboard', end: true, icon: () => <ListChecks size={S} strokeWidth={SW} /> },
      { label: 'The Book', to: '/clients', icon: () => <Users size={S} strokeWidth={SW} /> },
      { label: 'Pipeline', to: '/deals', end: true, icon: () => <TrendingUp size={S} strokeWidth={SW} /> },
      { label: 'Money', to: '/revenue', icon: () => <Banknote size={S} strokeWidth={SW} /> },
      { label: 'Calendar', to: '/calendar', end: true, icon: () => <Calendar size={S} strokeWidth={SW} /> },
    ],
  },
  {
    label: 'Everything else',
    collapsible: true,
    items: [
      { label: 'Signals', to: '/signals', icon: () => <Zap size={S} strokeWidth={SW} /> },
      { label: 'Prospects', to: '/prospects', icon: () => <Target size={S} strokeWidth={SW} /> },
      { label: 'Outreach', to: '/outreach', icon: () => <Send size={S} strokeWidth={SW} /> },
      { label: 'Portal access', to: '/portal-access', icon: () => <KeyRound size={S} strokeWidth={SW} /> },
      { label: 'Audience', to: '/audience', icon: () => <Share2 size={S} strokeWidth={SW} /> },
      { label: 'Contacts', to: '/contacts', icon: () => <BookUser size={S} strokeWidth={SW} /> },
      { label: 'Contracts', to: '/contracts', end: true, icon: () => <FileText size={S} strokeWidth={SW} /> },
      { label: 'Compliance', to: '/compliance', end: true, icon: () => <ShieldCheck size={S} strokeWidth={SW} /> },
      { label: 'Tasks', to: '/tasks', end: true, icon: () => <CheckSquare size={S} strokeWidth={SW} /> },
      { label: 'Inbox', to: '/client-inbox', icon: () => <Inbox size={S} strokeWidth={SW} /> },
      { label: 'Notifications', to: '/notifications', icon: () => <Bell size={S} strokeWidth={SW} /> },
      { label: 'Reports', to: '/reports', end: true, icon: () => <BarChart2 size={S} strokeWidth={SW} /> },
    ],
  },
]

export default function Sidebar() {
  const navigate = useNavigate()
  const { user } = useSession()
  const { open: openQuickLog } = useQuickLog()
  const [showSmartViews, setShowSmartViews] = useState(false)
  // "Everything else" is open unless this browser closed it. A per-viewer
  // convenience, nothing more, so storage that is missing means the default.
  const [moreOpen, setMoreOpen] = useState(() => {
    try { return localStorage.getItem(MORE_KEY) !== 'closed' } catch { return true }
  })
  useEffect(() => {
    try { localStorage.setItem(MORE_KEY, moreOpen ? 'open' : 'closed') } catch { /* private mode: the default is fine */ }
  }, [moreOpen])
  // Data health is admin-gated in-page; don't advertise it to everyone.
  const role = (user as any)?.role
  const isAgencyAdmin = role === 'admin' || role === 'superadmin'

  // Agency name sits under the wordmark in the rail. If it can't be read we show
  // nothing rather than a placeholder.
  const agencyId = (user as any)?.agency_id as string | undefined
  const agencyQ = useQuery({
    queryKey: ['agency', agencyId],
    queryFn: () => getAgency(agencyId!),
    enabled: !!agencyId,
    staleTime: 60 * 60 * 1000,
  })
  const agencyName = (agencyQ.data as any)?.agency_name ?? ''
  const agencyLogo = ((agencyQ.data as any)?.logo_url as string | undefined) || null

  // Unread count for the Notifications item. Refreshed every five minutes;
  // the page itself marks rows read and invalidates this key.
  const unreadQ = useQuery({
    queryKey: ['notifications', 'unread', user?.id],
    queryFn: () => getUnreadCount(user!.id),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  })
  const unreadCount = unreadQ.data ?? 0

  const handleLogout = async () => {
    await signOut()
    navigate('/login')
  }

  const getUserInitials = () => {
    if (!user) return 'U'
    const name = user.name || user.email || ''
    const parts = name.split(' ')
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase()
    }
    return name.slice(0, 2).toUpperCase()
  }

  const getUserRole = () => {
    if (!user) return 'User'
    const role = (user as any).role
    if (role === 'superadmin') return 'Super Admin'
    if (role === 'admin') return 'Administrator'
    if (role === 'agent') return 'Agent'
    if (role === 'assistant') return 'Assistant'
    if (role === 'read only') return 'Read Only'
    return 'Member'
  }

  return (
    <aside className={styles.sidebar}>
      <div className={styles.sidebarContent}>
        {/* Logo */}
        <div className={styles.logo}>
          {/* The shared icon asset, as MobileHeader and the public site use.
              This was a typed "R" in a brass square until Sep 2026. */}
          <img src="/icon-192.png" alt="" width={26} height={26} className={styles.logoIcon} />
          <div className={styles.logoTextContainer}>
            <span className={styles.logoText}>Reppr</span>
            <span className={styles.logoTier}>
              {agencyLogo && <img src={agencyLogo} alt="" className={styles.agencyMark} loading="lazy" />}
              {agencyName}
            </span>
          </div>
        </div>

        {/* Search affordance — opens the command palette (⌘K) */}
        <button
          className={styles.search}
          onClick={() => document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true }))}
          title="Search (⌘K)"
        >
          <Search size={13} strokeWidth={SW} />
          <span className={styles.searchLabel}>Search</span>
          <span className={styles.searchHint}>⌘K</span>
        </button>

        {/* Rail actions */}
        <button className={styles.smartViewsButton} onClick={openQuickLog} title="Quick Log (⌘L)">
          <span className={styles.navIcon}><Plus size={S} strokeWidth={SW} /></span>
          <span className={styles.navLabel}>Quick log</span>
          <span className={styles.searchHint}>⌘L</span>
        </button>

        {/* Navigation */}
        <nav className={styles.nav}>
          {/* Smart Views Button */}
          <button
            className={styles.smartViewsButton}
            onClick={() => setShowSmartViews(true)}
          >
            <span className={styles.navIcon}>
              <LayoutGrid size={S} strokeWidth={SW} />
            </span>
            <span className={styles.navLabel}>Smart Views</span>
          </button>

          {NAV_SECTIONS.map(section => {
            const visible = section.items.filter(item => !item.adminOnly || isAgencyAdmin)
            if (visible.length === 0) return null
            return (
              <div className={styles.navSection} key={section.label ?? 'root'}>
                {section.label && (section.collapsible
                  ? (
                    <button type="button" className={`${styles.navSectionLabel} ${styles.navSectionToggle}`} onClick={() => setMoreOpen(o => !o)} aria-expanded={moreOpen}>
                      {section.label}<span aria-hidden="true">{moreOpen ? '−' : '+'}</span>
                    </button>
                  )
                  : <div className={styles.navSectionLabel}>{section.label}</div>)}
                {(!section.collapsible || moreOpen) && visible.map(item => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      `${styles.navItem} ${isActive ? styles.active : ''}`
                    }
                  >
                    <span className={styles.navIcon}><item.icon /></span>
                    <span className={styles.navLabel}>{item.label}</span>
                    {(() => {
                      const badge = item.to === '/notifications' ? unreadCount : item.badge
                      return badge != null && badge > 0
                        ? <span className={styles.navBadge}>{badge > 99 ? '99+' : badge}</span>
                        : null
                    })()}
                  </NavLink>
                ))}
              </div>
            )
          })}
        </nav>

        {/* Bottom Fixed Section */}
        <div className={styles.bottomSection}>
          {(user as any)?.role === 'superadmin' && (
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                `${styles.navItem} ${isActive ? styles.active : ''}`
              }
            >
              <span className={styles.navIcon}><Settings2 size={S} strokeWidth={SW} /></span>
              <span className={styles.navLabel}>Admin</span>
            </NavLink>
          )}

          <NavLink
            to="/team"
            className={({ isActive }) =>
              `${styles.navItem} ${isActive ? styles.active : ''}`
            }
          >
            <span className={styles.navIcon}><Users2 size={S} strokeWidth={SW} /></span>
            <span className={styles.navLabel}>Team</span>
          </NavLink>

          <NavLink
            to="/settings"
            className={({ isActive }) =>
              `${styles.navItem} ${isActive ? styles.active : ''}`
            }
          >
            <span className={styles.navIcon}><Settings size={S} strokeWidth={SW} /></span>
            <span className={styles.navLabel}>Settings</span>
          </NavLink>

          <NavLink
            to="/help"
            className={({ isActive }) =>
              `${styles.navItem} ${isActive ? styles.active : ''}`
            }
          >
            <span className={styles.navIcon}><HelpCircle size={S} strokeWidth={SW} /></span>
            <span className={styles.navLabel}>Help</span>
          </NavLink>

          {/* User Profile */}
          <div className={styles.userCard}>
            <div className={styles.userAvatar}>{getUserInitials()}</div>
            <div className={styles.userInfo}>
              <div className={styles.userName}>{user?.name || user?.email || 'User'}</div>
              <div className={styles.userRole}>{getUserRole()}</div>
            </div>
            <button
              className={styles.logoutBtn}
              onClick={handleLogout}
              aria-label="Sign out"
              type="button"
            >
              <LogOut size={18} strokeWidth={SW} />
            </button>
          </div>
        </div>
      </div>

      <SmartViewLibrary
        isOpen={showSmartViews}
        onClose={() => setShowSmartViews(false)}
      />
    </aside>
  )
}
